import { useEffect, useMemo, useState } from 'react';
import { loadStripe, type Stripe, type StripeError } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AlertCircle, Loader2, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export interface PaymentConfirmModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	clientSecret: string | null;
	publishableKey: string;
	onConfirmed?: () => void;
}

export function PaymentConfirmModal({
	open,
	onOpenChange,
	clientSecret,
	publishableKey,
	onConfirmed,
}: PaymentConfirmModalProps) {
	const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
		if (!publishableKey) return null;
		return loadStripe(publishableKey);
	}, [publishableKey]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md" showCloseButton={true}>
				<DialogHeader className="p-4">
					<DialogTitle>Confirm payment</DialogTitle>
				</DialogHeader>

				<div className="px-4 pb-4">
					{!publishableKey && (
						<div className="flex flex-col items-center gap-3 py-10 text-center">
							<AlertCircle size={28} className="text-destructive" />
							<p className="text-base text-destructive">
								Stripe publishable key is not configured.
							</p>
							<Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
								Close
							</Button>
						</div>
					)}

					{publishableKey && !clientSecret && (
						<div className="flex flex-col items-center justify-center gap-3 py-10">
							<Loader2 size={24} className="animate-spin text-text-3" />
							<p className="text-base text-text-3">Preparing secure confirmation…</p>
						</div>
					)}

					{publishableKey && clientSecret && stripePromise && (
						<Elements
							stripe={stripePromise}
							options={{ clientSecret, appearance: { theme: 'night' } }}
						>
							<ConfirmForm
								onConfirmed={onConfirmed}
								onClose={() => onOpenChange(false)}
							/>
						</Elements>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function ConfirmForm({ onConfirmed, onClose }: { onConfirmed?: () => void; onClose: () => void }) {
	const stripe = useStripe();
	const elements = useElements();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);

	useEffect(() => {
		setError(null);
		setDone(false);
	}, []);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!stripe || !elements) return;

		setLoading(true);
		setError(null);

		const { error: confirmError } = (await stripe.confirmPayment({
			elements,
			redirect: 'if_required',
		})) as { error?: StripeError };

		if (confirmError) {
			setError(confirmError.message ?? 'Payment confirmation failed. Please try again.');
			setLoading(false);
			return;
		}

		setDone(true);
		setLoading(false);
		onConfirmed?.();
	};

	if (done) {
		return (
			<div className="flex flex-col items-center gap-4 py-10 text-center">
				<div className="text-lg font-medium text-text-1">Payment confirmed</div>
				<p className="text-base text-text-3">Your upgrade is complete.</p>
				<Button size="sm" onClick={onClose}>
					Close
				</Button>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit}>
			<div className="flex flex-col gap-4">
				<div className="flex items-center gap-2 rounded-lg border border-status-info/30 bg-status-info/10 px-3 py-2.5">
					<Lock size={14} className="shrink-0 text-status-info" />
					<p className="text-sm text-text-2">
						Your bank requires additional authentication to complete this charge.
					</p>
				</div>

				<PaymentElement />

				{error && (
					<div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
						<p className="text-sm text-destructive">{error}</p>
					</div>
				)}

				<div className="flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={onClose}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button type="submit" size="sm" disabled={!stripe || !elements || loading}>
						{loading ? 'Confirming…' : 'Confirm payment'}
					</Button>
				</div>

				<p className="text-center text-2xs text-text-4">Secured by Stripe</p>
			</div>
		</form>
	);
}
